import { useContext, useRef, useCallback, useEffect, useState } from 'react';
import { Spin, message } from 'antd';
import { MaterialManageContext } from '../hooks/MaterialManageContext';
import { AppContext } from '@utils/AppContext';
import Utils from '@utils/utils';
import PSAIModal from '@PSAIComponents/PSAIModal';
import ContentHeader from '../components/ContentHeader';
import MaterialCard from '../components/MaterialCard';
import FolderCard from '../components/FolderCard';
import EnterpriseEmpty from '../components/EnterpriseEmpty';
import MaterialEmpty from '../components/MaterialEmpty';
import MoveFolderModal from '../components/MoveFolderModal';
import MaterialUploadModal from '../components/MaterialUploadModal';
import API from '@api/api';
import styles from './index.module.scss';

const ContentArea = () => {
    const { globalOrgInfo } = useContext(AppContext);
    const {
        activeTab,
        activeMenuName,
        total,
        searchKeyword, setSearchKeyword,
        batchMode, setBatchMode,
        selectedList, setSelectedList,
        materialList,
        loading,
        loadingMore,
        hasMore,
        loadMoreMaterial,
        fetchMaterialList,
        fetchFolderTree,
        activeMenu,
        menuList,
        selectFolderWithExpand,
    } = useContext(MaterialManageContext);

    const [moveOpen, setMoveOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);

    const refreshList = () => {
        const folderId = activeMenu === 'refImage_all' ? '' : (activeMenu || '');
        fetchMaterialList({
            scope: activeTab,
            folderId,
            keyword: searchKeyword,
            pageIndex: 1,
        });
        fetchFolderTree(activeTab);
    };

    const handleBatchShare = () => {
        if (selectedList.length === 0) return;
        setShareOpen(true);
    };

    const handleBatchMove = () => {
        if (selectedList.length === 0) return;
        setMoveOpen(true);
    };

    const handleBatchDelete = () => {
        if (selectedList.length === 0) return;
        PSAIModal.confirm({
            content: (
                <div style={{ textAlign: 'center' }}>
                    <p>确定要删除选中的 {selectedList.length} 个参考图吗？</p>
                    <p style={{ color: '#FF3B30' }}>此操作不可复原，请确认是否删除</p>
                </div>
            ),
            onOk: () => {
                return API.materialResDelete({ resIds: selectedList })
                    .then((res) => {
                        if (res.code === 0) {
                            message.success('删除成功');
                            setBatchMode(false);
                            setSelectedList([]);
                            refreshList();
                        } else {
                            message.error(res.message || '删除失败');
                        }
                    })
                    .catch(() => {
                        message.error('删除失败，请稍后重试');
                    });
            },
        });
    };

    const handleBatchCancel = () => {
        setBatchMode(false);
        setSelectedList([]);
    };

    const handleMoveSuccess = () => {
        setMoveOpen(false);
        setBatchMode(false);
        setSelectedList([]);
        refreshList();
    };

    const handleOpenUpload = () => {
        const status = Utils.checkLoginStatus(() => handleOpenUpload());
        if (!status) return;
        setUploadOpen(true);
    };

    const scrollRef = useRef(null);

    // 滚动加载更多
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (loading || materialList.length === 0) return;
        // 距离底部 200px 时触发加载
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
            loadMoreMaterial();
        }
    }, [loading, materialList.length, loadMoreMaterial]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop = 0;
    }, [activeMenu, activeTab, searchKeyword]);

    // 企业素材 tab：当前是个人账户才显示空白页，企业账户正常展示内容
    if (activeTab === 'enterprise' && (globalOrgInfo?.isPersonal || !localStorage.getItem('token'))) {
        return (
            <div className={styles.ContentArea}>
                <EnterpriseEmpty />
            </div>
        );
    }

    return (
        <div className={styles.ContentArea}>
            <ContentHeader
                title={activeMenuName}
                total={total}
                searchKeyword={searchKeyword}
                onSearch={setSearchKeyword}
                batchMode={batchMode}
                onBatchMode={setBatchMode}
                selectedList={selectedList}
                onBatchShare={handleBatchShare}
                onBatchMove={handleBatchMove}
                onBatchDelete={handleBatchDelete}
                onBatchCancel={handleBatchCancel}
                onUpload={handleOpenUpload}
            />
            <div ref={scrollRef} className={`${styles.materialGrid} _beauty_scrollbar`}>
                {loading && materialList.length === 0 ? (
                    <div className={styles.loadingWrap}>
                        <Spin />
                    </div>
                ) : materialList.length === 0 ? (
                    <MaterialEmpty isSearch={!!searchKeyword} onUpload={handleOpenUpload} />
                ) : (
                    <>
                        {materialList.map(item => {
                            const isSelected = selectedList.includes(item.id);
                            const handleSelect = (id) => {
                                if (selectedList.includes(id)) {
                                    setSelectedList(prev => prev.filter(v => v !== id));
                                } else {
                                    setSelectedList(prev => [...prev, id]);
                                }
                            };

                            if (item.type === 'folder') {
                                return (
                                    <FolderCard
                                        key={item.id}
                                        item={item}
                                        selected={isSelected}
                                        batchMode={batchMode}
                                        onSelect={handleSelect}
                                        onClick={() => selectFolderWithExpand(item.id, item.name)}
                                    />
                                );
                            }

                            return (
                                <MaterialCard
                                    key={item.id}
                                    item={item}
                                    selected={isSelected}
                                    batchMode={batchMode}
                                    onSelect={handleSelect}
                                />
                            );
                        })}
                        {loadingMore && (
                            <div className={styles.loadingMoreWrap}>
                                <Spin size="small" />
                            </div>
                        )}
                    </>
                )}
            </div>
            <MoveFolderModal
                open={moveOpen}
                mode="res"
                menuList={menuList}
                resIds={selectedList}
                zIndex={1010}
                onSuccess={handleMoveSuccess}
                onCancel={() => setMoveOpen(false)}
            />
            <MoveFolderModal
                open={shareOpen}
                mode="share"
                resIds={selectedList}
                shareScope="enterprise"
                zIndex={1010}
                onSuccess={() => {
                    setShareOpen(false);
                    setBatchMode(false);
                    setSelectedList([]);
                    refreshList();
                }}
                onCancel={() => setShareOpen(false)}
            />
            <MaterialUploadModal
                open={uploadOpen}
                scope={activeTab}
                folderId={activeMenu === 'refImage_all' ? '' : (activeMenu || '')}
                sampleType={activeMenuName?.includes('背景') ? 'scene' : 'model'}
                onSuccess={refreshList}
                onCancel={() => setUploadOpen(false)}
            />
        </div>
    );
};

export default ContentArea;
